import { LoadingButton } from "@mui/lab";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { CampaignsContext } from "contexts/Campaigns";
import { useContext, useEffect, useState } from "react";
import { Campaign } from "schemas/dashboard";

interface Props {
  campaign: Campaign | null;
  open: boolean;
  handleClose: () => void;
}

export default function RenameCampaignDialog({
  campaign,
  open,
  handleClose,
}: Props) {
  const { renameCampaign } = useContext(CampaignsContext);
  const { setAlert } = useContext(AlertContext);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) setName(campaign?.name ?? "");
  }, [open, campaign?.name]);

  const save = async () => {
    const trimmed = name.trim();
    if (!campaign?.id || !trimmed || trimmed === campaign.name) {
      handleClose();
      return;
    }
    setLoading(true);
    const ok = await renameCampaign(campaign.id, trimmed);
    setLoading(false);
    if (ok) {
      setAlert("Campaign renamed", "success");
      handleClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 18 }}>
        Rename campaign
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          value={name}
          disabled={loading}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              save();
            }
          }}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleClose}
          disabled={loading}
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={loading}
          onClick={save}
          disabled={!name.trim()}
          sx={{ textTransform: "none" }}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
